/**
 * Provider Directory Detail Component
 * Displays comprehensive provider information in a modal or page
 */

import { X, MapPin, Phone, Mail, Globe, Star, Navigation, CheckCircle, AlertCircle } from 'lucide-react';
import { Provider } from './ProviderResults';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

interface ProviderDetailProps {
  provider: Provider | null;
  onClose: () => void;
  onGetDirections: (provider: Provider) => void;
  onSelectProvider?: (provider: Provider) => void;
  showSelectButton?: boolean;
  onSaveProvider?: (provider: Provider) => void;
  isProviderSaved?: boolean;
}

export function ProviderDetail({ provider, onClose, onGetDirections, onSelectProvider, showSelectButton = false, onSaveProvider, isProviderSaved = false }: ProviderDetailProps) {
  if (!provider) return null;

  const getQualityLabel = (score?: number) => {
    if (!score) return 'Not rated';
    if (score >= 80) return 'High quality data';
    if (score >= 60) return 'Good quality data';
    return 'Data needs update';
  };

  const formatHours = (hours: any) => {
    if (!hours) return <span className="text-gray-500 text-sm">Hours not available</span>;

    // Validate that hours is an object
    if (typeof hours !== 'object' || Array.isArray(hours)) {
      return <span className="text-gray-500 text-sm">Hours not available</span>;
    }

    // Assuming hours is stored as JSON with day keys
    const daysOfWeek = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const hourElements = daysOfWeek.map(day => {
      const dayHours = hours[day];
      if (!dayHours) return null;
      return (
        <div key={day} className="flex justify-between text-sm">
          <span className="capitalize font-medium">{day}</span>
          <span className="text-gray-600">{dayHours}</span>
        </div>
      );
    }).filter(Boolean);

    // Return a fragment to properly wrap the array
    return hourElements.length > 0 ? <>{hourElements}</> : <span className="text-gray-500 text-sm">Hours not available</span>;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {provider.organization_name}
            </h2>
            <div className="flex flex-wrap gap-2">
              <Badge variant="info" size="md">
                {typeof provider.organization_type === 'string'
                  ? provider.organization_type
                  : typeof provider.organization_type === 'object'
                  ? JSON.stringify(provider.organization_type)
                  : 'Unknown'}
              </Badge>
              <Badge
                variant={
                  !provider.data_quality_score ? "default" :
                  provider.data_quality_score >= 80 ? "success" :
                  provider.data_quality_score >= 60 ? "warning" :
                  "danger"
                }
                size="md"
              >
                <Star className="w-4 h-4 mr-1 flex-shrink-0" />
                {getQualityLabel(provider.data_quality_score)}
              </Badge>
              {provider.distance_miles != null && (
                <Badge variant="success" size="md">
                  <Navigation className="w-4 h-4 mr-1 flex-shrink-0" />
                  {provider.distance_miles.toFixed(1)} miles away
                </Badge>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </Button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Contact Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <div className="flex flex-col">
                  <span className="text-gray-900">{provider.street_address}</span>
                  <span className="text-gray-600">{provider.city}, {provider.state} {provider.zip_code}</span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-gray-400 flex-shrink-0" />
                <a href={`tel:${provider.primary_phone}`} className="text-blue-600 hover:text-blue-700">
                  {provider.primary_phone}
                </a>
              </div>
              {provider.primary_email && (
                <div className="flex items-center space-x-3">
                  <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${provider.primary_email}`} className="text-blue-600 hover:text-blue-700">
                    {provider.primary_email}
                  </a>
                </div>
              )}
              {provider.website && (
                <div className="flex items-center space-x-3">
                  <Globe className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  <a
                    href={provider.website.startsWith('http') ? provider.website : `https://${provider.website}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-700"
                  >
                    Visit Website
                  </a>
                </div>
              )}
            </div>
          </section>

          {/* Services Offered */}
          {provider.services && Array.isArray(provider.services) && provider.services.length > 0 && (
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Services Offered</h3>
              <div className="flex flex-wrap gap-2">
                {provider.services.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium bg-blue-50 text-blue-700 rounded-md border border-blue-200"
                  >
                    {typeof service === 'string' ? service : String(service)}
                  </span>
                ))}
              </div>
            </section>
          )}

          {/* Insurance & Payment */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Insurance & Payment</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Insurance Accepted</h4>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2">
                    {provider.accepts_medicaid ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={provider.accepts_medicaid ? 'text-gray-900' : 'text-gray-500'}>
                      Medicaid
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {provider.accepts_medicare ? (
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                    ) : (
                      <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    )}
                    <span className={provider.accepts_medicare ? 'text-gray-900' : 'text-gray-500'}>
                      Medicare
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Payment Options</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>Sliding fee scale available</p>
                  <p>Payment plans offered</p>
                  <p>Call for specific details</p>
                </div>
              </div>
            </div>
          </section>

          {/* Languages & Accessibility */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Languages & Accessibility</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {provider.languages_spoken && Array.isArray(provider.languages_spoken) && provider.languages_spoken.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">Languages Spoken</h4>
                  <div className="flex flex-wrap gap-2">
                    {provider.languages_spoken.map((language, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 text-xs font-medium bg-white text-gray-700 rounded border border-gray-200"
                      >
                        {typeof language === 'string' ? language : String(language)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Accessibility</h4>
                <div className="flex items-center space-x-2">
                  {provider.wheelchair_accessible ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      <span className="text-gray-900">Wheelchair accessible</span>
                    </>
                  ) : (
                    <>
                      <AlertCircle className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <span className="text-gray-500">Accessibility information not available</span>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>

          {/* Hours of Operation */}
          {provider.hours_of_operation && (
            <section>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Hours of Operation</h3>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="space-y-2">
                  {formatHours(provider.hours_of_operation)}
                </div>
              </div>
            </section>
          )}

          {/* Data Quality Information */}
          {provider.data_quality_score && (
            <section className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-start space-x-3">
                <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="text-sm font-semibold text-blue-900 mb-1">Data Quality</h4>
                  <p className="text-sm text-blue-800">
                    This provider's information has a quality score of {provider.data_quality_score}/100.
                    {provider.data_quality_score < 70 && ' We recommend calling to verify details before visiting.'}
                  </p>
                </div>
              </div>
            </section>
          )}
        </div>

        {/* Footer Actions */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-6 flex flex-col sm:flex-row gap-3">
          {showSelectButton && onSelectProvider && (
            <Button
              variant="primary"
              onClick={() => {
                onSelectProvider(provider);
                onClose();
              }}
              icon={<CheckCircle className="w-5 h-5 flex-shrink-0" />}
              className="flex-1 bg-gradient-to-r from-purple-600 to-violet-600 hover:from-purple-700 hover:to-violet-700 shadow-lg hover:shadow-xl"
            >
              Select This Provider
            </Button>
          )}
          {onSaveProvider && (
            <Button
              variant={isProviderSaved ? "outline" : "outline"}
              onClick={() => onSaveProvider(provider)}
              icon={<CheckCircle className={`w-5 h-5 flex-shrink-0 ${isProviderSaved ? 'fill-green-700' : ''}`} />}
              className={`flex-1 ${
                isProviderSaved
                  ? 'bg-green-100 text-green-700 border-2 border-green-500 hover:bg-green-200'
                  : 'text-blue-600 border-2 border-blue-600 hover:bg-blue-50'
              }`}
              title={isProviderSaved ? 'Saved to My Providers' : 'Save to My Providers'}
            >
              {isProviderSaved ? 'Saved to My Providers' : 'Save to My Providers'}
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => onGetDirections(provider)}
            icon={<Navigation className="w-5 h-5 flex-shrink-0" />}
            className="flex-1"
          >
            Get Directions
          </Button>
          <Button
            variant="secondary"
            onClick={() => window.location.href = `tel:${provider.primary_phone}`}
            icon={<Phone className="w-5 h-5 flex-shrink-0" />}
            className="flex-1 bg-v2accent hover:bg-v2accent/90 text-white"
          >
            Call Now
          </Button>
          {provider.website && (
            <Button
              variant="outline"
              onClick={() => window.open(
                provider.website?.startsWith('http') ? provider.website : `https://${provider.website}`,
                '_blank',
                'noopener,noreferrer'
              )}
              icon={<Globe className="w-5 h-5 flex-shrink-0" />}
              className="flex-1"
            >
              Visit Website
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
